import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === Number(id));

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                影片未找到
              </h1>
              <p className="text-foreground-500 mb-6">抱歉，您要查看的影片不存在或已下架。</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-foreground-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.category === movie.category && m.id !== movie.id)
    .slice(0, 4);

  const categoryNames: Record<string, string> = {
    dianying: '电影',
    dianshiju: '电视剧',
    zongyi: '综艺',
    dongman: '动漫',
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - ${movie.year}年${movie.region}${categoryNames[movie.category] || ''} | 木瓜影院`,
    description: movie.description,
    author: {
      '@type': 'Organization',
      name: '木瓜影院',
    },
    publisher: {
      '@type': 'Organization',
      name: '木瓜影院',
    },
    datePublished: movie.updateTime,
    dateModified: movie.updateTime,
    url: `https://www.muguayingyuan.com/article/${movie.id}`,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.muguayingyuan.com/' },
      { '@type': 'ListItem', position: 2, name: categoryNames[movie.category] || movie.category, item: `https://www.muguayingyuan.com/category/${movie.category}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <Navbar />
      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryNames[movie.category] || movie.category, path: `/category/${movie.category}` },
                { label: movie.title },
              ]}
            />

            <article className="mt-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 w-full md:w-[300px]">
                  <div className="rounded-xl overflow-hidden bg-background-200 aspect-[2/3]">
                    <img
                      src={movie.coverImage}
                      alt={`${movie.title} - 木瓜影院高清${categoryNames[movie.category] || ''}`}
                      title={`${movie.title} - 木瓜影院高清${categoryNames[movie.category] || ''}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    {movie.title}
                  </h1>

                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-primary-500/10 text-primary-600 text-sm font-semibold">
                      <i className="ri-star-fill text-sm"></i>
                      {movie.rating}
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="text-sm text-foreground-500">{categoryNames[movie.category] || movie.category}</span>
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-lg bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">
                      <strong>导演</strong>
                    </h4>
                    <p className="text-sm text-foreground-600">{movie.director}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">
                      <strong>主演</strong>
                    </h4>
                    <p className="text-sm text-foreground-600">
                      {movie.actors.join(' / ')}
                    </p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-2">
                      <strong>影片简介</strong>
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {movie.description}
                    </p>
                  </div>

                  <div className="mb-6 p-4 rounded-xl bg-primary-500/5 border border-primary-500/10">
                    <p className="text-sm text-primary-600 italic">
                      <strong>一句话推荐：</strong>{movie.summary}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <a
                      href="#"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-foreground-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-fill text-lg"></i>
                      立即播放
                    </a>
                    <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-600 font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line text-lg"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {relatedMovies.length > 0 && (
              <section className="mt-16">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${movie.category}`}
                    className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{categoryNames[movie.category] || '影片'}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
                  data-product-shop
                >
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} {...m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}